import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatEin } from "@/lib/ein";
import { toast } from "sonner";
import { ChevronLeft, Copy, CheckCircle2, AlertTriangle } from "lucide-react";

const GROUP_LABEL = {
    equipment: "Equipment",
    company: "Company",
    guarantor: "Guarantor",
    references: "References",
    uploads: "Uploads",
    signatures: "Signatures",
};

const EQUIPMENT_OPTIONS = [
    { value: "reefer", label: "Reefer" },
    { value: "dry_van", label: "Dry Van" },
    { value: "conestoga", label: "Conestoga" },
    { value: "flatbed", label: "Flatbed" },
    { value: "step_deck", label: "Step Deck" },
    { value: "other", label: "Other" },
];

const STATE_OPTIONS = [
    { value: "required", label: "Required" },
    { value: "optional", label: "Optional" },
    { value: "hidden", label: "Hidden" },
];

const STATE_PILL = {
    required: "bg-blue-900 text-white",
    optional: "bg-slate-100 text-slate-800",
    hidden: "bg-slate-50 text-slate-400 line-through",
};

export default function CreateInvitePage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [catalog, setCatalog] = useState(null);
    const [loading, setLoading] = useState(true);

    const [customerEmail, setCustomerEmail] = useState("");
    const [customerName, setCustomerName] = useState("");
    const [expiresInDays, setExpiresInDays] = useState(7);
    const [equipmentPreselect, setEquipmentPreselect] = useState("");
    const [customerSearch, setCustomerSearch] = useState("");
    const [customerResults, setCustomerResults] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [config, setConfig] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [createdInvite, setCreatedInvite] = useState(null);
    // Soft guardrail for the "31-required-field invite" incident.
    // overConstrained = fields whose catalog `default_required=false` but
    // which the admin flipped to `required` on this invite. Shows a chip
    // inline + a confirmation modal at submit time. Catalog is the source
    // of truth — we read default_required from /fields/catalog rather than
    // hardcoding which fields are "normally optional".
    const [pendingConfirm, setPendingConfirm] = useState(false);

    // Search returning customers
    useEffect(() => {
        if (!customerSearch.trim()) {
            setCustomerResults([]);
            return;
        }
        const timer = setTimeout(async () => {
            try {
                const { data } = await api.get("/admin/customers", {
                    params: { q: customerSearch, status_filter: "active", limit: 5 },
                });
                setCustomerResults(data.items || []);
            } catch {
                setCustomerResults([]);
            }
        }, 250);
        return () => clearTimeout(timer);
    }, [customerSearch]);

    const pickCustomer = (c) => {
        setSelectedCustomer(c);
        if (c?.primary_contact_email) setCustomerEmail(c.primary_contact_email);
        if (c?.company_name) setCustomerName(c.company_name);
        setCustomerSearch("");
        setCustomerResults([]);
    };

    const clearSelectedCustomer = () => {
        setSelectedCustomer(null);
    };

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/fields/catalog");
                setCatalog(data);
                setConfig(data.default_config);
            } catch (err) {
                toast.error(formatApiError(err));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const grouped = useMemo(() => {
        if (!catalog) return {};
        return catalog.fields.reduce((acc, f) => {
            (acc[f.group] = acc[f.group] || []).push(f);
            return acc;
        }, {});
    }, [catalog]);

    const setFieldState = (key, val) => setConfig((c) => ({ ...c, [key]: val }));

    // Over-constrained list — catalog `default_required=false` BUT admin
    // flipped them to `required`. Cheap memo over the same catalog/config
    // we already render.
    const overConstrainedFields = useMemo(() => {
        if (!catalog) return [];
        return catalog.fields.filter(
            (f) => !f.default_required && config[f.key] === "required"
        );
    }, [catalog, config]);
    const overConstrainedKeys = useMemo(
        () => new Set(overConstrainedFields.map((f) => f.key)),
        [overConstrainedFields]
    );

    const applyPreset = (preset) => {
        if (!catalog) return;
        if (preset === "all_required") {
            setConfig(Object.fromEntries(catalog.fields.map((f) => [f.key, "required"])));
        } else if (preset === "minimal") {
            setConfig(
                Object.fromEntries(
                    catalog.fields.map((f) => [
                        f.key,
                        f.group === "company" || f.group === "guarantor"
                            ? f.default_required
                                ? "required"
                                : "optional"
                            : "hidden",
                    ])
                )
            );
        } else if (preset === "defaults") {
            setConfig(catalog.default_config);
        }
    };

    const doCreateInvite = async () => {
        setError("");
        setSubmitting(true);
        try {
            const { data } = await api.post("/admin/invites", {
                customer_email: customerEmail,
                customer_name: customerName,
                field_config: config,
                expires_in_days: Number(expiresInDays) || 7,
                customer_id: selectedCustomer?.id || null,
                equipment_type: equipmentPreselect || null,
            });
            toast.success("Invite created and mock email logged");
            setCreatedInvite(data);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const onSubmit = (e) => {
        e.preventDefault();
        // If admin has flipped any normally-optional field to required,
        // open the soft-confirmation modal instead of creating immediately.
        if (overConstrainedFields.length > 0) {
            setPendingConfirm(true);
            return;
        }
        doCreateInvite();
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(createdInvite.invite_url);
            toast.success("Link copied");
        } catch {
            toast.error("Could not copy");
        }
    };

    if (loading) {
        return (
            <DashboardShell>
                <div className="text-sm text-slate-500" data-testid="create-invite-loading">Loading field catalog…</div>
            </DashboardShell>
        );
    }

    if (createdInvite) {
        return (
            <DashboardShell>
                <div className="max-w-2xl fade-up" data-testid="invite-created-success">
                    <Link to="/admin/invites" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-6">
                        <ChevronLeft size={14} /> All invites
                    </Link>
                    <div className="inline-flex items-center gap-2 text-emerald-700 mb-3">
                        <CheckCircle2 size={20} />
                        <span className="text-xs font-medium tracking-[0.18em] uppercase">Invite sent</span>
                    </div>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                        {createdInvite.customer_name}'s invite is ready.
                    </h1>
                    <p className="mt-3 text-sm text-slate-600">
                        A mock email has been logged for <strong>{createdInvite.customer_email}</strong>. View it on the Mock emails page or share the link directly:
                    </p>

                    <Card className="mt-6">
                        <CardContent className="p-5">
                            <Label>Invite link</Label>
                            <div className="mt-2 flex items-center gap-2">
                                <Input
                                    data-testid="created-invite-url"
                                    readOnly
                                    value={createdInvite.invite_url}
                                    className="font-mono text-xs"
                                />
                                <Button onClick={copyLink} data-testid="copy-created-invite" className="bg-blue-900 hover:bg-blue-800 gap-1.5">
                                    <Copy size={14} /> Copy
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    <div className="mt-8 flex gap-3">
                        <Button asChild variant="outline">
                            <Link to="/admin/invites" data-testid="back-to-invites">Back to invites</Link>
                        </Button>
                        {user?.role === "super_admin" && (
                            <Button asChild variant="outline">
                                <Link to="/admin/mock-emails" data-testid="view-mock-emails">View mock emails</Link>
                            </Button>
                        )}
                    </div>
                </div>
            </DashboardShell>
        );
    }

    return (
        <DashboardShell>
            <div className="fade-up">
                <Link to="/admin/invites" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-6">
                    <ChevronLeft size={14} /> All invites
                </Link>
                <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900 mb-3">
                    New invite
                </p>
                <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                    Send a customer application
                </h1>
                <p className="mt-3 max-w-2xl text-sm text-slate-600 leading-relaxed">
                    Pre-fill the customer's contact info, choose which fields are required, optional, or hidden, and we'll generate a unique invite link.
                </p>
            </div>

            <form onSubmit={onSubmit} className="mt-8 space-y-8" data-testid="create-invite-form">
                {/* Customer info */}
                <Card>
                    <CardContent className="p-6 space-y-5">
                        <h2 className="font-display text-lg font-semibold text-slate-900">Customer details</h2>

                        {/* Returning customer picker */}
                        <div className="space-y-2" data-testid="returning-customer-picker">
                            <Label htmlFor="returning_search">Returning customer (optional)</Label>
                            {selectedCustomer ? (
                                <div className="flex items-center justify-between rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm" data-testid="returning-customer-selected">
                                    <div>
                                        <span className="font-medium text-blue-900">{selectedCustomer.company_name}</span>
                                        {selectedCustomer.primary_contact_email && (
                                            <span className="text-blue-900/70 ml-2">· {selectedCustomer.primary_contact_email}</span>
                                        )}
                                        <p className="text-xs text-blue-900/60 mt-0.5">Prefill will be offered to this customer.</p>
                                    </div>
                                    <Button type="button" variant="outline" size="sm" onClick={clearSelectedCustomer} data-testid="clear-returning-customer">
                                        Change
                                    </Button>
                                </div>
                            ) : (
                                <>
                                    <Input
                                        id="returning_search"
                                        data-testid="returning-customer-search"
                                        placeholder="Search by company, EIN, or email"
                                        value={customerSearch}
                                        onChange={(e) => setCustomerSearch(e.target.value)}
                                    />
                                    {customerResults.length > 0 && (
                                        <ul className="rounded-md border border-slate-200 divide-y divide-slate-100 max-h-48 overflow-y-auto" data-testid="returning-customer-results">
                                            {customerResults.map((c) => (
                                                <li key={c.id}>
                                                    <button
                                                        type="button"
                                                        onClick={() => pickCustomer(c)}
                                                        data-testid={`returning-pick-${c.id}`}
                                                        className="w-full text-left px-3 py-2 hover:bg-slate-50"
                                                    >
                                                        <div className="text-sm font-medium text-slate-900">{c.company_name}</div>
                                                        <div className="text-xs text-slate-500">
                                                            {c.primary_contact_email || "—"}{c.ein_tax_id ? ` · ${formatEin(c.ein_tax_id)}` : ""}
                                                        </div>
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    <p className="text-xs text-slate-500">Leave empty for a brand-new customer; selection enables prefill.</p>
                                </>
                            )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <div className="space-y-2">
                                <Label htmlFor="customer_name">Customer name</Label>
                                <Input
                                    id="customer_name"
                                    data-testid="invite-customer-name-input"
                                    required
                                    value={customerName}
                                    onChange={(e) => setCustomerName(e.target.value)}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="customer_email">Customer email</Label>
                                <Input
                                    id="customer_email"
                                    data-testid="invite-customer-email-input"
                                    type="email"
                                    required
                                    value={customerEmail}
                                    onChange={(e) => setCustomerEmail(e.target.value)}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="expires_in_days">Expires in (days)</Label>
                                <Input
                                    id="expires_in_days"
                                    data-testid="invite-expires-input"
                                    type="number"
                                    min={1}
                                    max={90}
                                    value={expiresInDays}
                                    onChange={(e) => setExpiresInDays(e.target.value)}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="equipment_preselect">Equipment type (optional preselect)</Label>
                                <select
                                    id="equipment_preselect"
                                    data-testid="invite-equipment-preselect"
                                    value={equipmentPreselect}
                                    onChange={(e) => setEquipmentPreselect(e.target.value)}
                                    className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                                >
                                    <option value="">No preselect — customer picks</option>
                                    {EQUIPMENT_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Field config */}
                <Card>
                    <CardContent className="p-6">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <h2 className="font-display text-lg font-semibold text-slate-900">Field configuration</h2>
                            <div className="flex flex-wrap gap-2">
                                <Button type="button" size="sm" variant="outline" onClick={() => applyPreset("defaults")} data-testid="preset-defaults">
                                    Reset to defaults
                                </Button>
                                <Button type="button" size="sm" variant="outline" onClick={() => applyPreset("all_required")} data-testid="preset-all-required">
                                    All required
                                </Button>
                                <Button type="button" size="sm" variant="outline" onClick={() => applyPreset("minimal")} data-testid="preset-minimal">
                                    Minimal
                                </Button>
                            </div>
                        </div>

                        <div className="mt-6 space-y-8">
                            {Object.entries(grouped).map(([group, fields]) => (
                                <section key={group}>
                                    <h3 className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500 mb-3">
                                        {GROUP_LABEL[group] || group}
                                    </h3>
                                    <div className="divide-y divide-slate-100 border border-slate-200 rounded-md bg-white">
                                        {fields.map((f) => (
                                            <div
                                                key={f.key}
                                                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3"
                                                data-testid={`field-row-${f.key}`}
                                            >
                                                <div className="min-w-0">
                                                    <div className="font-medium text-slate-900 text-sm flex items-center gap-2">
                                                        {f.label}
                                                        {overConstrainedKeys.has(f.key) && (
                                                            <span
                                                                data-testid={`over-constrained-chip-${f.key}`}
                                                                className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800 ring-1 ring-amber-200"
                                                                title="Normally optional — may slow the customer down."
                                                            >
                                                                <AlertTriangle size={10} className="shrink-0" />
                                                                Normally optional
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-slate-500">
                                                        {f.type}
                                                        {f.default_required ? " · default required" : ""}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1.5" role="radiogroup">
                                                    {STATE_OPTIONS.map((opt) => {
                                                        const active = config[f.key] === opt.value;
                                                        return (
                                                            <button
                                                                type="button"
                                                                key={opt.value}
                                                                role="radio"
                                                                aria-checked={active}
                                                                onClick={() => setFieldState(f.key, opt.value)}
                                                                data-testid={`field-${f.key}-${opt.value}`}
                                                                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                                                    active
                                                                        ? STATE_PILL[opt.value]
                                                                        : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
                                                                }`}
                                                            >
                                                                {opt.label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {error && (
                    <div data-testid="create-invite-error" className="rounded-md bg-red-50 border border-red-100 px-3 py-2 text-sm text-red-700">
                        {error}
                    </div>
                )}

                <div className="flex gap-3 sticky bottom-4 bg-slate-50/80 backdrop-blur p-3 rounded-md border border-slate-200">
                    <Button asChild variant="outline" data-testid="invite-cancel">
                        <Link to="/admin/invites">Cancel</Link>
                    </Button>
                    <Button
                        type="submit"
                        disabled={submitting}
                        onClick={() => {}}
                        data-testid="invite-submit-button"
                        className="bg-blue-900 hover:bg-blue-800"
                    >
                        {submitting ? "Sending…" : "Create & send invite"}
                    </Button>
                </div>
            </form>

            <AlertDialog open={pendingConfirm} onOpenChange={setPendingConfirm}>
                <AlertDialogContent data-testid="over-constrained-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirm extra required fields</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-3">
                                <p>
                                    You've marked these normally-optional fields as required:
                                </p>
                                <ul
                                    data-testid="over-constrained-field-list"
                                    className="list-disc list-inside text-sm text-slate-900 space-y-0.5 max-h-40 overflow-y-auto"
                                >
                                    {overConstrainedFields.map((f) => (
                                        <li key={f.key} data-testid={`over-constrained-item-${f.key}`}>
                                            {f.label}
                                        </li>
                                    ))}
                                </ul>
                                <p className="text-sm text-slate-600">
                                    This may slow your customer down. Continue?
                                </p>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="over-constrained-cancel">
                            Go back
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="over-constrained-confirm"
                            onClick={() => {
                                setPendingConfirm(false);
                                doCreateInvite();
                            }}
                        >
                            Yes, create invite
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </DashboardShell>
    );
}
